/**
 * ParallaxSection.tsx — Renders foreground content over a CSS parallax background image.
 *
 * JSON trigger: `uiCompType.method: "renderParallax"`
 *
 * **How the parallax effect works:**
 * Uses `background-attachment: fixed` (the "zero-JS" CSS approach). The background
 * image stays fixed in the viewport while the rest of the page scrolls over it,
 * creating the illusion of depth. On iOS (which ignores `background-attachment: fixed`
 * for performance reasons) we fall back to `scroll` so the image still looks good.
 *
 * **Layout layers (outermost → innermost):**
 * ```
 * <Box sx={outerStyles}>              ← parallax container (background-attachment: fixed)
 *   <Box sx={overlayStyles}>          ← optional colour/gradient overlay (default: transparent)
 *   <Panel sectionData={content}>     ← Panel owns all foreground layout; style via content.uiCompType.parentStyles
 * </Box>
 * ```
 *
 * **Data fields (all optional except `backgroundImageUrl`):**
 *  - `sectionData.backgroundImageUrl`                    — parallax background image URL
 *  - `sectionData.content`                               — any Panel-compatible SectionData; Panel handles all
 *                                                          internal layout, text, buttons, nested sections, etc.
 *  - `sectionData.content.uiCompType.parentStyles`       — outer styles for the foreground content (width, padding, bg…)
 *  - `sectionData.content.uiCompType.mParentStyles`      — mobile override for content parentStyles
 *  - `sectionData.uiCompType.styles`                     — overrides for the outer parallax container
 *  - `sectionData.uiCompType.mStyles`                    — mobile overrides (falls back to scroll attachment)
 *  - `sectionData.uiCompType.options.overlayColor`       — CSS colour/gradient for the overlay layer
 *  - `sectionData.uiCompType.options.backgroundPosition` — CSS background-position (default: "center")
 *
 * **Mobile:** `background-attachment` is forced to `scroll` on mobile because iOS Safari
 * does not support `fixed` attachment inside scrollable containers. `mStyles` and
 * `mContainerStyles` are merged on top of their desktop counterparts.
 *
 * **Accessibility:** The container receives `role="img"` and `aria-label` from
 * `sectionData.title` so screen readers can describe the background image.
 */
'use client';

import React from 'react';
import { Box } from '@mui/material';
import { SectionData, ComponentRenderProps } from './index';
import Panel from './Panel';
import { useDeviceType } from '@/hooks/useDeviceType';

interface ParallaxProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const Parallax = ({ sectionData, props }: ParallaxProps): JSX.Element | null => {
    const deviceType = useDeviceType();
    const isMobile = deviceType === 'Mobile';

    const {
        backgroundImageUrl,
        content,
        title,
        uiCompType,
    } = sectionData;

    const overlayColor: string = uiCompType?.options?.overlayColor ?? 'transparent';
    const bgPosition: string = uiCompType?.options?.backgroundPosition ?? 'center';

    // Outer parallax container styles
    const baseOuterStyles: React.CSSProperties = {
        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
        // CSS parallax: image stays fixed, content scrolls over it
        backgroundAttachment: 'fixed',
        backgroundPosition: bgPosition,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        minHeight: '500px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...uiCompType?.styles,
    };

    // On mobile, iOS ignores background-attachment:fixed → fall back to scroll
    const mobileOuterStyles: React.CSSProperties = {
        ...baseOuterStyles,
        backgroundAttachment: 'scroll',
        ...uiCompType?.mStyles,
    };

    const outerStyles = isMobile ? mobileOuterStyles : baseOuterStyles;

    return (
        <Box
            sx={outerStyles as any}
            role="img"
            aria-label={title || 'Parallax background section'}
        >
            {/* Colour / gradient overlay layer */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: overlayColor,
                    zIndex: 1,
                    pointerEvents: 'none',
                }}
            />

            {/* Panel owns all foreground layout — style via content.uiCompType.parentStyles */}
            {content && (() => {
                // Panel reads uiCompType.styles for its outer <div>, not parentStyles.
                // Merge parentStyles → styles so Panel picks them up without an extra wrapper Box.
                const contentUiCompType = (content as SectionData).uiCompType;
                const mergedContent: SectionData = {
                    ...(content as SectionData),
                    uiCompType: {
                        ...contentUiCompType,
                        styles: {
                            position: 'relative',
                            zIndex: 2,
                            ...contentUiCompType?.parentStyles,
                            ...contentUiCompType?.styles,
                        },
                        mStyles: {
                            ...contentUiCompType?.parentStyles,
                            ...contentUiCompType?.mParentStyles,
                            ...contentUiCompType?.mStyles,
                        },
                    },
                };
                return <Panel sectionData={mergedContent} props={props} />;
            })()}
        </Box>
    );
};

export default Parallax;
